import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { typography } from '@/common/styles/typographyTokens';
import { semanticColors } from '@/common/styles/colorTokens';
import { sizes } from '@/common/styles/sizes';
import Checkbox, { CheckboxProps } from './Checkbox';

export interface CheckboxGroupProps {
  /**
   * 체크박스 그룹의 이름
   */
  name?: string;
  
  /**
   * 체크박스 그룹에 적용할 레이블
   */
  label?: React.ReactNode;
  
  /**
   * 체크박스 옵션 목록
   */
  options: Array<{
    label: React.ReactNode;
    value: string;
    disabled?: boolean;
  }>;
  
  /**
   * 현재 선택된 값 목록
   */
  value?: string[];
  
  /**
   * 기본 선택된 값 목록
   */
  defaultValue?: string[];
  
  /**
   * 값이 변경될 때 호출되는 함수
   */
  onChange?: (checkedValues: string[]) => void;
  
  /**
   * 에러 상태 여부
   */
  error?: boolean;
  
  /**
   * 에러 메시지
   */
  errorMessage?: string;
  
  /**
   * 비활성화 여부
   */
  disabled?: boolean;
  
  /**
   * 필수 항목 여부
   */
  required?: boolean;
  
  /**
   * 체크박스 그룹 방향 (가로/세로)
   */
  direction?: 'horizontal' | 'vertical';
  
  /**
   * 체크박스 컴포넌트에 전달할 추가 props
   */
  checkboxProps?: Omit<CheckboxProps, 'value' | 'checked' | 'onChange' | 'disabled'>;
  
  /**
   * 추가 CSS 클래스
   */
  className?: string;
}

/**
 * CheckboxGroup 컴포넌트
 * 
 * 여러 체크박스를 그룹으로 관리하는 컴포넌트입니다.
 * 가로/세로 방향 배치, 에러 상태 표시, 전체 비활성화 등을 지원합니다.
 */
export const CheckboxGroup = ({
  name,
  label,
  options = [],
  value,
  defaultValue,
  onChange,
  error = false,
  errorMessage,
  disabled = false,
  required = false,
  direction = 'vertical',
  checkboxProps,
  className,
}: CheckboxGroupProps) => {
  const [internalValue, setInternalValue] = useState<string[]>(value || defaultValue || []);
  
  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);
  
  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    const updatedValues = checked
      ? [...internalValue, optionValue]
      : internalValue.filter(val => val !== optionValue);
      
    setInternalValue(updatedValues);
    onChange?.(updatedValues);
  };
  
  return (
    <CheckboxGroupContainer className={className}>
      {label && (
        <GroupLabel disabled={disabled}>
          {label}
          {required && <RequiredMark>*</RequiredMark>}
        </GroupLabel>
      )}
      
      <OptionsContainer direction={direction}>
        {options.map(option => (
          <Checkbox
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
            checked={internalValue.includes(option.value)}
            onChange={(checked) => handleCheckboxChange(option.value, checked)}
            disabled={disabled || option.disabled}
            error={error}
            {...checkboxProps}
          />
        ))}
      </OptionsContainer>
      
      {error && errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </CheckboxGroupContainer>
  );
};

// 스타일드 컴포넌트
const CheckboxGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.gap[8]};
`;

const GroupLabel = styled.div<{ disabled?: boolean }>`
  ${typography.paragraph.p2.medium};
  color: ${props => props.disabled 
    ? semanticColors.text.disabled 
    : semanticColors.text.primary};
  margin-bottom: ${sizes.margin[4]};
`;

const OptionsContainer = styled.div<{ direction: 'horizontal' | 'vertical' }>`
  display: flex;
  flex-direction: ${props => props.direction === 'horizontal' ? 'row' : 'column'};
  gap: ${props => props.direction === 'horizontal' ? sizes.gap[16] : sizes.gap[12]};
  flex-wrap: ${props => props.direction === 'horizontal' ? 'wrap' : 'nowrap'};
`;

const RequiredMark = styled.span`
  color: ${semanticColors.border.dangerPrimary};
  margin-left: ${sizes.margin[4]};
`;

const ErrorMessage = styled.div`
  ${typography.paragraph.p2.medium};
  color: ${semanticColors.text.onDanger.secondary};
  margin-top: ${sizes.margin[4]};
`;

export default CheckboxGroup; 